import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "react-toastify";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import dayjs from "dayjs";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CSpinner,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CCardHeader,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilChartPie,
  cilCalendar,
  cilVideo,
  cilClock,
  cilSpeedometer,
} from "@coreui/icons";
import { formatHoursMins } from "../utils";

registerLocale("ru", ru);

const StatsModal = ({ visible, user, onClose }) => {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    activePreset: "month",
  });

  useEffect(() => {
    if (!visible || !user) return;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        let url = `/api/users/${user.id}/markup-logs/`;
        const params = new URLSearchParams();

        if (filters.startDate)
          params.append("start_date", filters.startDate.toISOString());
        if (filters.endDate)
          params.append("end_date", filters.endDate.toISOString());
        if (params.toString()) url += `?${params.toString()}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLogs(res.data);
      } catch (err) {
        toast.error("Не удалось загрузить логи");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [user, filters.startDate, filters.endDate, visible]);

  useEffect(() => {
    if (visible) {
      setFilters({
        startDate: dayjs().startOf("month").toDate(),
        endDate: dayjs().endOf("day").toDate(),
        activePreset: "month",
      });
    }
  }, [visible]);

  const setQuickFilter = (preset) => {
    const now = dayjs();
    const map = {
      today: { start: now.startOf("day"), end: now.endOf("day") },
      week: {
        start: now.subtract(6, "day").startOf("day"),
        end: now.endOf("day"),
      },
      month: { start: now.startOf("month"), end: now.endOf("day") },
      all: { start: null, end: null },
    };

    const target = map[preset];
    setFilters({
      startDate: target.start ? target.start.toDate() : null,
      endDate: target.end ? target.end.toDate() : null,
      activePreset: preset,
    });
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    let adjustedEnd = end;

    if (start && end) {
      adjustedEnd = new Date(end);
      adjustedEnd.setHours(23, 59, 59, 999);
    }

    setFilters({
      startDate: start,
      endDate: adjustedEnd,
      activePreset: "custom",
    });
  };

  if (!user) return null;

  return (
    <CModal alignment="center" size="xl" visible={visible} onClose={onClose}>
      <CModalHeader className="bg-light border-bottom-0 pb-3">
        <CModalTitle className="d-flex align-items-center">
          <CIcon icon={cilChartPie} className="me-2 text-primary" size="xl" />
          Статистика разметки:{" "}
          <span className="ms-2 fw-bold text-primary">{user.username}</span>
        </CModalTitle>
      </CModalHeader>

      <CModalBody className="p-4 bg-light">
        <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center bg-white p-3 rounded shadow-sm border mb-4 gap-3">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span className="fw-bold text-muted me-2 d-flex align-items-center">
              <CIcon icon={cilCalendar} className="me-1" /> Период:
            </span>
            <div className="btn-group">
              {["today", "week", "month", "all"].map((preset) => (
                <CButton
                  key={preset}
                  color="primary"
                  variant={filters.activePreset === preset ? "" : "outline"}
                  onClick={() => setQuickFilter(preset)}
                >
                  {preset.toUpperCase()}
                </CButton>
              ))}
            </div>
          </div>
          <div className="d-flex align-items-center">
            <DatePicker
              selectsRange
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={handleDateChange}
              isClearable
              className="form-control form-control-sm text-center fw-semibold"
              dateFormat="dd.MM.yyyy"
              locale="ru"
              placeholderText="Выберите даты"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center p-5">
            <CSpinner color="primary" />
          </div>
        ) : (
          <>
            <CRow className="mb-4 g-3">
              <CCol md={4}>
                <CCard className="h-100 border-0 border-start border-start-4 border-start-primary shadow-sm">
                  <CCardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted text-uppercase fw-bold small mb-1">
                        Видео (часов)
                      </div>
                      <div className="fs-2 fw-bold text-dark lh-1">
                        {logs?.total_folders || 0}
                      </div>
                    </div>
                    <CIcon
                      icon={cilVideo}
                      size="3xl"
                      className="text-primary opacity-50"
                    />
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol md={4}>
                <CCard className="h-100 border-0 border-start border-start-4 border-start-success shadow-sm">
                  <CCardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted text-uppercase fw-bold small mb-1">
                        Рабочее время
                      </div>
                      <div className="fs-2 fw-bold text-success lh-1">
                        {formatHoursMins(logs?.total_time_sec)}
                      </div>
                    </div>
                    <CIcon
                      icon={cilClock}
                      size="3xl"
                      className="text-success opacity-50"
                    />
                  </CCardBody>
                </CCard>
              </CCol>
              <CCol md={4}>
                <CCard className="h-100 border-0 border-start border-start-4 border-start-info shadow-sm">
                  <CCardBody className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted text-uppercase fw-bold small mb-1">
                        Ср. время на 1 час
                      </div>
                      <div className="fs-2 fw-bold text-info lh-1">
                        {formatHoursMins(
                          (logs?.total_time_sec || 0) /
                            (logs?.total_folders || 1),
                        )}
                      </div>
                    </div>
                    <CIcon
                      icon={cilSpeedometer}
                      size="3xl"
                      className="text-info opacity-50"
                    />
                  </CCardBody>
                </CCard>
              </CCol>
            </CRow>

            <CCard className="shadow-sm border-0">
              <CCardHeader className="bg-white fw-bold">
                Детализация действий
              </CCardHeader>
              <div style={{ maxHeight: "450px", overflowY: "auto" }}>
                <CTable hover align="middle" className="mb-0 border-top-0">
                  <CTableHead
                    color="light"
                    style={{ position: "sticky", top: 0, zIndex: 1 }}
                  >
                    <CTableRow>
                      <CTableHeaderCell>Дата</CTableHeaderCell>
                      <CTableHeaderCell>Устройство</CTableHeaderCell>
                      <CTableHeaderCell>Машин</CTableHeaderCell>
                      <CTableHeaderCell className="text-end">
                        Время
                      </CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {logs?.logs?.length > 0 ? (
                      logs.logs.map((log) => (
                        <CTableRow key={log.id}>
                          <CTableDataCell>
                            {new Date(log.date).toLocaleDateString("ru-RU")}
                          </CTableDataCell>
                          <CTableDataCell>{log.device}</CTableDataCell>
                          <CTableDataCell>{log.segments}</CTableDataCell>
                          <CTableDataCell className="text-end fw-bold text-success">
                            {formatHoursMins(log.time_sec)}
                          </CTableDataCell>
                        </CTableRow>
                      ))
                    ) : (
                      <CTableRow>
                        <CTableDataCell colSpan={4} className="text-center p-4">
                          Нет данных
                        </CTableDataCell>
                      </CTableRow>
                    )}
                  </CTableBody>
                </CTable>
              </div>
            </CCard>
          </>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Закрыть
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

StatsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

StatsModal.defaultProps = {
  user: null,
};

export default StatsModal;